import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Lock, Mail } from '@/components/icons';
import logoutIcon from '@/assets/logout-power.png';
import { useAuth } from '@/auth/useAuth';
import { ChangePasswordModal } from '@/auth/ChangePasswordModal';
import { SayHiAvatar } from './SayHiAvatar';
import { useIsMobile } from './useIsMobile';
import { queueIntro } from './IntroSplash';

/** SUPER_ADMIN → "Super Admin" */
const prettyRole = (code: string) =>
  code.toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

/** Top-bar user button: waving avatar that opens a panel with the user's details, Change password and Logout. */
export function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile(); // the phone top bar is 48px tall, the desktop one 64px

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={user.fullName}
        // Round avatar that sits over the left end of a slim name pill.
        className="user-chip group relative inline-flex items-center leading-none text-[var(--text-secondary)] transition hover:text-accent"
      >
        <span className="user-chip-pill topbar-btn hidden h-8 items-center gap-1.5 rounded-full border pr-3 md:inline-flex" style={{ paddingLeft: 48 / 2 + 12 }}>
          <span className="max-w-[200px] truncate text-xs font-bold text-[var(--text-primary)]">{user.fullName}</span>
          <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
        <span className="user-chip-avatar relative z-[1] rounded-full md:absolute md:left-0 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2">
          <SayHiAvatar size={isMobile ? 32 : 48} />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-1rem)] overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-2xl"
        >
          <div className="flex items-center gap-3 border-b border-[var(--border-color)] bg-accent/[0.06] p-3.5">
            <SayHiAvatar size={64} />
            <div className="min-w-0">
              <div className="truncate text-sm font-extrabold text-[var(--text-primary)]">{user.fullName}</div>
              <div className="truncate text-[11px] text-[var(--text-muted)]">@{user.username}</div>
            </div>
          </div>
          <div className="space-y-2 p-3.5 text-[12px]">
            <div className="flex items-center gap-2 text-[var(--text-secondary)]">
              <Mail size={13} className="shrink-0 text-[var(--text-muted)]" />
              <span className="truncate" title={user.email || undefined}>{user.email || 'No email on file'}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-bold text-accent">{prettyRole(user.roleCode)}</span>
              {user.department && (
                <span className="rounded-full bg-[var(--bg-soft)] px-2 py-0.5 text-[10px] font-bold text-[var(--text-secondary)]">{user.department}</span>
              )}
            </div>
          </div>
          <div className="border-t border-[var(--border-color)] p-2">
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); setPwOpen(true); }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-bold text-[var(--text-secondary)] transition hover:bg-accent/10 hover:text-accent"
            >
              <Lock size={14} strokeWidth={2.25} /> Change password
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); queueIntro('goodbye', user.fullName); void logout(); }}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] font-bold text-danger transition hover:bg-danger/10"
            >
              <img src={logoutIcon} alt="" className="h-[18px] w-[18px] shrink-0" /> Logout
            </button>
          </div>
        </div>
      )}
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
